import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { supabase, BACKEND_URL } from '../../supabaseClient';
import './ProfileSettings.css';

const ProfileSettings = () => {
  const { profile: contextProfile, loading: contextLoading } = useOutletContext();
  
  // --- UI State ---
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // --- Form State ---
  const [fullNameInput, setFullNameInput] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [birthdayInput, setBirthdayInput] = useState('');
  const [roleInput, setRoleInput] = useState('');
  const [genderInput, setGenderInput] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  
  const fileInputRef = useRef(null);

  // --- SYNC DATA ---
  useEffect(() => {
    if (contextProfile) {
      setFullNameInput(contextProfile.full_name || '');
      setUsernameInput(contextProfile.username || '');
      setBirthdayInput(contextProfile.birthday || '');
      setRoleInput(contextProfile.professional_role || '');
      setGenderInput(contextProfile.gender || '');
      setAvatarUrl(contextProfile.avatar_url || '');
    }
  }, [contextProfile]);

  // --- AVATAR UPLOAD ---
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    const uploadToast = toast.loading("Uploading...");
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const base64Image = reader.result;
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch(`${BACKEND_URL}/api/profile/avatar`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ imageBase64: base64Image })
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.error);

        toast.success("Avatar updated!");
        setAvatarUrl(result.avatar_url);
        toast.dismiss(uploadToast);
      };
    } catch (error) {
      console.error(error);
      toast.error("Upload failed");
      toast.dismiss(uploadToast);
    }
  };

  // --- SAVE PROFILE ---
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const updates = {
        full_name: fullNameInput,
        username: usernameInput,
        birthday: birthdayInput,
        professional_role: roleInput,
        gender: genderInput
      };

      const response = await fetch(`${BACKEND_URL}/api/profile`, { 
        method: 'PUT', 
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${session.access_token}` 
        }, 
        body: JSON.stringify(updates) 
      });
      
      if (!response.ok) throw new Error("Update failed");
      
      toast.success("Profile saved!");
      setIsProfileOpen(false);
    } catch (err) { 
      toast.error(err.message); 
    } finally { 
      setIsUpdating(false); 
    }
  };

  if (contextLoading) return <div className="loading-state">Loading...</div>;

  return (
    <div className="profile-page-container fade-in">
      
      {/* --- NEW HEADER (Matching Dashboard Banner) --- */}
      <div className="profile-banner">
        <div>
          <h1>Personal Information</h1>
          <p>Manage your identity and details.</p>
        </div>
        
        {/* AVATAR (Positioned like the Plan Badge) */}
        <div className="avatar-section" onClick={() => isProfileOpen && fileInputRef.current.click()}>
            <div className={`avatar-circle ${isProfileOpen ? 'editable' : ''}`}>
              {avatarUrl ? (
                 <img src={avatarUrl} alt="Avatar" />
              ) : (
                 <span className="avatar-initials">{fullNameInput ? fullNameInput[0] : '?'}</span>
              )}
              {isProfileOpen && <div className="avatar-overlay">📷</div>}
            </div>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} hidden />
        </div>
      </div>

      {/* --- FORM CARD --- */}
      <div className="account-card">
        <form onSubmit={handleProfileUpdate}>
          <div className="account-card-content">
            
            {/* ROW 1: Name & Username */}
            <div className="form-grid-2col">
              <div className="input-group">
                <label className="theme-label">Full Name*</label>
                <input 
                  type="text" 
                  value={fullNameInput} 
                  onChange={(e) => setFullNameInput(e.target.value)} 
                  placeholder="Enter full name" 
                  disabled={!isProfileOpen}
                  className="input-field"
                />
              </div>
              <div className="input-group">
                <label className="theme-label">Username*</label>
                <input 
                  type="text" 
                  value={usernameInput} 
                  onChange={(e) => setUsernameInput(e.target.value)} 
                  placeholder="Enter username" 
                  disabled={!isProfileOpen}
                  className="input-field"
                />
              </div>
            </div>

            {/* ROW 2: Birthday & Role */}
            <div className="form-grid-2col mt-4">
              <div className="input-group">
                <label className="theme-label">Birthday</label>
                <input 
                  type="date" 
                  value={birthdayInput} 
                  onChange={(e) => setBirthdayInput(e.target.value)} 
                  disabled={!isProfileOpen}
                  className="input-field"
                />
              </div>
              <div className="input-group">
                <label className="theme-label">Professional Role</label>
                <select 
                    value={roleInput} 
                    onChange={(e) => setRoleInput(e.target.value)} 
                    disabled={!isProfileOpen}
                    className="input-field"
                >
                  <option value="">Select a role...</option>
                  <option value="Graphic Designer">Graphic Designer</option>
                  <option value="E-commerce Owner">E-commerce Owner</option>
                  <option value="Agency">Agency</option>
                  <option value="Marketing Specialist">Marketing Specialist</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* ROW 3: Gender */}
            <div className="form-grid-2col mt-4">
                <div className="input-group">
                    <label className="theme-label">Gender</label>
                    <select 
                        value={genderInput} 
                        onChange={(e) => setGenderInput(e.target.value)} 
                        disabled={!isProfileOpen}
                        className="input-field"
                    >
                    <option value="">Prefer not to say</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Non-binary">Non-binary</option>
                    </select>
                </div>
                <div></div>
            </div>

          </div>

          {/* FOOTER ACTIONS */}
          <div className="account-card-footer">
            {isProfileOpen ? (
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setIsProfileOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary-theme" disabled={isUpdating}>
                  {isUpdating ? "Saving..." : "Save Changes"}
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-primary-theme" onClick={() => setIsProfileOpen(true)}>
                Edit Profile
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileSettings;